import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import PageAdvice from "../components/PageAdvice";
import {
  Clock, CircleNotch, ArrowsClockwise, CheckCircle, XCircle, Database,
  CalendarCheck, Lightning, WarningCircle, HardDrives,
} from "@phosphor-icons/react";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Sydney" }) : "—");

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
      <div className="flex items-center gap-2 text-neutral-500"><Icon size={16} weight="bold" /><span className="text-xs uppercase tracking-widest font-semibold">{label}</span></div>
      <p className={`mt-2 font-heading text-2xl font-black tracking-tighter ${accent || "text-[#0A0A0A]"}`}>{value}</p>
    </div>
  );
}

export default function Scheduler() {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [legacy, setLegacy] = useState(null);
  const [legacyRunning, setLegacyRunning] = useState(false);
  const [savingZones, setSavingZones] = useState(false);

  const load = async () => {
    const [s, r] = await Promise.all([api.get("/scheduler"), api.get("/scheduler/runs")]);
    setStatus(s.data);
    setRuns(r.data.runs);
  };
  const loadLegacy = async () => {
    try {
      const { data } = await api.get("/legacy/sync");
      setLegacy(data);
    } catch (e) { console.debug("Legacy sync status unavailable (admin-only / not configured)", e); }
  };
  useEffect(() => { load(); loadLegacy(); }, []);

  const runNow = async () => {
    setRunning(true);
    setError("");
    try {
      await api.post("/scheduler/run-now");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const runLegacy = async () => {
    setLegacyRunning(true);
    setError("");
    try {
      await api.post("/legacy/sync/run");
      await loadLegacy();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLegacyRunning(false);
    }
  };

  const toggleZone = async (zoneId) => {
    if (!legacy) return;
    const current = legacy.config.zones || [];
    const next = current.includes(zoneId) ? current.filter((z) => z !== zoneId) : [...current, zoneId];
    setSavingZones(true);
    try {
      await api.put("/legacy/sync/zones", { zones: next });
      await loadLegacy();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingZones(false);
    }
  };

  if (!status) {
    return <Layout><div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="scheduler-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Automation</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Data Scheduler</h1>
          </div>
          <button data-testid="run-now-button" onClick={runNow} disabled={running} className="flex items-center gap-2 rounded-sm bg-[#0055FF] px-5 py-2.5 text-base font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-50">
            {running ? <CircleNotch size={18} className="animate-spin" /> : <ArrowsClockwise size={18} weight="bold" />} Run snapshot now
          </button>
        </div>
        <p className="mt-3 max-w-2xl text-base text-neutral-600">
          A daily snapshot saves group & client data locally so reports load instantly and don't consume Aroflo API limits.
        </p>

        {error && <div className="mt-4 rounded-sm border border-[#DC2626]/30 bg-[#DC2626]/5 px-4 py-3 text-sm text-[#DC2626]" data-testid="scheduler-error">{error}</div>}

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Clock} label="Schedule" value={status.schedule.label} accent="text-[#0055FF]" />
          <Stat icon={status.enabled ? CheckCircle : XCircle} label="Status" value={status.enabled ? "Active" : "Off"} accent={status.enabled ? "text-[#059669]" : "text-[#DC2626]"} />
          <Stat icon={Database} label="Clients Cached" value={status.snapshots.clients_cached} />
          <Stat icon={CalendarCheck} label="Next Run" value={fmtTime(status.next_run)} />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
            <h3 className="mb-3 font-heading text-lg font-black tracking-tight">How it works</h3>
            <ul className="space-y-3 text-sm text-neutral-600">
              <li className="flex gap-2"><Clock size={18} className="mt-0.5 shrink-0 text-[#0055FF]" /> Every day at <strong className="text-[#0A0A0A]">4:00 AM Sydney</strong>, the system pulls fresh data for all clients in your Client Groups.</li>
              <li className="flex gap-2"><Database size={18} className="mt-0.5 shrink-0 text-[#0055FF]" /> Data is saved to local tables. Reports read from this cache — <strong className="text-[#0A0A0A]">no API limits used</strong>.</li>
              <li className="flex gap-2"><Lightning size={18} className="mt-0.5 shrink-0 text-[#D97706]" /> On any report you can hit <strong className="text-[#0A0A0A]">PULL LIVE DATA</strong> for an instant refresh — limited to once per hour per report.</li>
            </ul>
            <div className="mt-4 rounded-sm bg-neutral-50 p-3 text-xs text-neutral-500">
              Newest snapshot: {fmtTime(status.snapshots.newest_pulled_at)}
            </div>
          </div>

          <div className="lg:col-span-2 border border-neutral-200 bg-white shadow-sm rounded-sm">
            <div className="border-b border-neutral-200 px-5 py-3"><h3 className="font-heading text-lg font-black tracking-tight">Recent Runs</h3></div>
            {runs.length === 0 ? (
              <div className="flex h-40 flex-col items-center justify-center gap-2 text-neutral-500" data-testid="runs-empty"><WarningCircle size={24} className="text-neutral-300" /> No runs yet — trigger one above.</div>
            ) : (
              <div className="overflow-x-auto" data-testid="runs-table">
                <table className="w-full border-collapse text-sm">
                  <thead><tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                    <th className="px-5 py-2.5 text-left">Started (Sydney)</th><th className="px-5 py-2.5 text-left">Trigger</th><th className="px-5 py-2.5 text-right">Clients</th><th className="px-5 py-2.5 text-left">Status</th>
                  </tr></thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.run_id} className="border-b border-neutral-100 even:bg-neutral-50/60">
                        <td className="px-5 py-2.5 font-mono text-neutral-600">{fmtTime(r.started_at)}</td>
                        <td className="px-5 py-2.5"><span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-xs font-semibold capitalize">{r.trigger}</span></td>
                        <td className="px-5 py-2.5 text-right font-mono">{r.clients_pulled}</td>
                        <td className="px-5 py-2.5">
                          <span className={`inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-semibold ${r.status === "completed" ? "bg-[#059669]/10 text-[#059669]" : r.status === "running" ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-[#DC2626]/10 text-[#DC2626]"}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {legacy && (
          <div className="mt-8 border border-neutral-200 bg-white shadow-sm rounded-sm" data-testid="legacy-sync-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <HardDrives size={20} weight="bold" className="text-[#0055FF]" />
                <div>
                  <h3 className="font-heading text-lg font-black tracking-tight">Legacy API Sync</h3>
                  <p className="text-xs text-neutral-500">Pulls the selected Legacy Aroflo zones into the local database each daily run.</p>
                </div>
              </div>
              <button
                data-testid="legacy-sync-run-button"
                onClick={runLegacy}
                disabled={legacyRunning || legacy.running}
                className="flex items-center gap-2 rounded-sm border border-[#0055FF] px-4 py-2 text-sm font-semibold text-[#0055FF] transition-all duration-200 hover:bg-[#0055FF]/5 disabled:opacity-50"
              >
                {(legacyRunning || legacy.running) ? <CircleNotch size={16} className="animate-spin" /> : <ArrowsClockwise size={16} weight="bold" />} Sync legacy now
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 p-5 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">Zones to sync {savingZones && <CircleNotch size={12} className="ml-1 inline animate-spin" />}</p>
                <div className="grid grid-cols-2 gap-2" data-testid="legacy-sync-zones">
                  {legacy.available_zones.map((z) => {
                    const on = legacy.config.zones.includes(z.id);
                    return (
                      <label key={z.id} className={`flex cursor-pointer items-center gap-2 rounded-sm border px-3 py-2 text-sm transition-all ${on ? "border-[#0055FF] bg-[#0055FF]/5 text-[#0A0A0A]" : "border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`} data-testid={`legacy-zone-toggle-${z.id}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleZone(z.id)} disabled={savingZones} />
                        {z.label || z.id}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-500">Stored snapshots</p>
                {Object.keys(legacy.snapshots).length === 0 ? (
                  <div className="rounded-sm bg-neutral-50 p-4 text-sm text-neutral-500" data-testid="legacy-snapshots-empty">No legacy data synced yet — run a sync above.</div>
                ) : (
                  <table className="w-full border-collapse text-sm" data-testid="legacy-snapshots-table">
                    <thead><tr className="border-b border-neutral-200 text-xs uppercase tracking-wider text-neutral-500">
                      <th className="py-2 text-left">Zone</th><th className="py-2 text-right">Records</th><th className="py-2 text-left pl-4">Last pulled</th>
                    </tr></thead>
                    <tbody>
                      {Object.entries(legacy.snapshots).map(([zone, s]) => (
                        <tr key={zone} className="border-b border-neutral-100">
                          <td className="py-2 font-semibold capitalize">{zone}</td>
                          <td className="py-2 text-right font-mono">{s.count}{s.truncated ? "+" : ""}</td>
                          <td className="py-2 pl-4 font-mono text-neutral-600">{fmtTime(s.pulled_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {legacy.last_run && (
                  <p className="mt-3 text-xs text-neutral-500">
                    Last run: {fmtTime(legacy.last_run.started_at)} · <span className="capitalize">{legacy.last_run.status}</span>
                    {legacy.last_run.errors?.length > 0 && <span className="text-[#DC2626]"> · {legacy.last_run.errors.length} error(s)</span>}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        <PageAdvice items={[
          "The daily sync runs automatically at 4:00 AM (Australia/Sydney): it refreshes snapshots, the clients & technicians directories, and the selected Legacy zones.",
          "\u201cRun now\u201d triggers the full daily job on demand; the Legacy zone sync respects the zones you tick above.",
          "Timesheets are synced separately from the Technicians page (they can be large), so they are not part of this daily run by default.",
        ]} />
      </div>
    </Layout>
  );
}
